// src/components/settings/SystemSettings.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { Box, CircularProgress } from '@mui/material';
import {
  referenceDataAPI,
  activityLogRetentionAPI,
  ActivityLogRetentionStatus,
  quoteCleanupAPI,
  QuoteCleanupStatus,
} from '../../services/api';
import { useGlobalToast } from '../../App';
import { useTheme } from '@mui/material/styles';
import { StyledSwitch } from '../forms/shared/FormElements';
import { AccountActionButton } from '../common/Buttons';
import SettingsHeaderRow from './shared/SettingsHeaderRow';
import LoadingSpinner from '../common/LoadingSpinner';
import About from './shared/About';
import Backups from './Backups';
import {
  SettingsBlock,
  SettingsCard,
  SectionLabel,
  SettingRow,
  SettingLabel,
  SettingDescription,
  Section,
  RetentionGrid,
  RetentionStat,
  StatLabel,
  StatValue,
  formatDateTime,
} from './shared/SystemSettingsElements';

interface SystemSettingsState {
  showResolutionIndicator: boolean;
}

const SystemSettings: React.FC = () => {
  const { showSuccess, showError } = useGlobalToast();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState<SystemSettingsState>({
    showResolutionIndicator: false,
  });
  const [savingField, setSavingField] = useState<string | null>(null);

  const [retention, setRetention] = useState<ActivityLogRetentionStatus | null>(
    null
  );
  const [retentionLoading, setRetentionLoading] = useState(false);
  const [sweeping, setSweeping] = useState(false);

  const [quoteCleanup, setQuoteCleanup] = useState<QuoteCleanupStatus | null>(
    null
  );
  const [quoteCleanupLoading, setQuoteCleanupLoading] = useState(false);

  const loadQuoteCleanup = useCallback(async () => {
    try {
      setQuoteCleanupLoading(true);
      const status = await quoteCleanupAPI.getStatus();
      setQuoteCleanup(status);
    } catch {
      // silent
    } finally {
      setQuoteCleanupLoading(false);
    }
  }, []);

  const loadRetention = useCallback(async () => {
    try {
      setRetentionLoading(true);
      const status = await activityLogRetentionAPI.getStatus();
      setRetention(status);
    } catch {
      // silent
    } finally {
      setRetentionLoading(false);
    }
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const dbSettings = await referenceDataAPI.getSystemSettings();
        setSettings({
          showResolutionIndicator: dbSettings.showResolutionIndicator,
        });
      } catch {
        showError('Failed to load system settings');
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
    loadRetention();
    loadQuoteCleanup();
  }, []);

  const saveSettings = async (
    updated: SystemSettingsState,
    fieldName: string
  ) => {
    try {
      setSavingField(fieldName);
      await referenceDataAPI.updateSystemSettings({
        showResolutionIndicator: updated.showResolutionIndicator,
      });
      window.dispatchEvent(new Event('system-settings-updated'));
      return true;
    } catch (error: any) {
      if (error.isConflict) {
        showError('Settings were modified by another user. Please refresh.');
      } else {
        showError('Failed to save setting');
      }
      return false;
    } finally {
      setSavingField(null);
    }
  };

  const handleToggle = async (field: 'showResolutionIndicator') => {
    const updated = { ...settings, [field]: !settings[field] };
    setSettings(updated);
    const success = await saveSettings(updated, field);
    if (success) {
      showSuccess(
        `Resolution indicator ${updated[field] ? 'enabled' : 'disabled'}`
      );
    } else {
      setSettings((prev) => ({ ...prev, [field]: !prev[field] }));
    }
  };

  const handleSweepNow = async () => {
    try {
      setSweeping(true);
      const result = await activityLogRetentionAPI.sweepNow();
      showSuccess(
        result.deleted > 0
          ? `Sweep complete. Removed ${result.deleted} expired entr${result.deleted === 1 ? 'y' : 'ies'}.`
          : 'Sweep complete. Nothing to remove.'
      );
      await loadRetention();
    } catch {
      showError('Sweep failed');
    } finally {
      setSweeping(false);
    }
  };

  if (loading) {
    return <LoadingSpinner message="Loading System Settings..." size={80} />;
  }

  return (
    <Box>
      <SettingsHeaderRow title="System Settings" />

      <About />

      <SettingsBlock>
        <SettingsCard elevation={0}>
          <Section>
            <SectionLabel>Developer</SectionLabel>
            <SettingRow>
              <Box>
                <SettingLabel>Show Resolution Indicator</SettingLabel>
                <SettingDescription>
                  Display a small badge in the bottom-right corner showing the
                  current viewport resolution on hover
                </SettingDescription>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {savingField === 'showResolutionIndicator' && (
                  <CircularProgress size={18} />
                )}
                <StyledSwitch
                  checked={settings.showResolutionIndicator}
                  onChange={() => handleToggle('showResolutionIndicator')}
                  disabled={savingField === 'showResolutionIndicator'}
                />
              </Box>
            </SettingRow>
          </Section>
        </SettingsCard>
      </SettingsBlock>

      <Backups />

      <SettingsBlock>
        <SettingsCard elevation={0}>
          <Section>
            <SectionLabel>Activity Log Retention</SectionLabel>
            <SettingDescription sx={{ mb: 1 }}>
              Activity log entries older than the retention period are
              automatically deleted. POPIA-compliant data minimisation.
            </SettingDescription>

            {retentionLoading && !retention ? (
              <Box sx={{ py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : retention ? (
              <>
                <RetentionGrid>
                  <RetentionStat>
                    <StatLabel>Retention</StatLabel>
                    <StatValue>{retention.retentionDays} days</StatValue>
                  </RetentionStat>
                  <RetentionStat>
                    <StatLabel>Sweep every</StatLabel>
                    <StatValue>{retention.sweepIntervalHours} hours</StatValue>
                  </RetentionStat>
                  <RetentionStat>
                    <StatLabel>Last sweep</StatLabel>
                    <StatValue>
                      {formatDateTime(retention.lastSweepAt)}
                    </StatValue>
                  </RetentionStat>
                  <RetentionStat>
                    <StatLabel>Next sweep</StatLabel>
                    <StatValue>
                      {formatDateTime(retention.nextSweepAt)}
                    </StatValue>
                  </RetentionStat>
                  <RetentionStat>
                    <StatLabel>Last removed</StatLabel>
                    <StatValue>
                      {retention.lastSweepDeletedCount ?? 0}
                    </StatValue>
                  </RetentionStat>
                  <RetentionStat>
                    <StatLabel>Currently expired</StatLabel>
                    <StatValue>{retention.expiredCount}</StatValue>
                  </RetentionStat>
                </RetentionGrid>

                <Box
                  sx={{
                    mt: 1.5,
                    display: 'flex',
                    gap: 1,
                    flexDirection: { xs: 'column', sm: 'row' },
                  }}
                >
                  <AccountActionButton
                    variant="contained"
                    onClick={handleSweepNow}
                    disabled={sweeping}
                    bgColor={theme.palette.primary.main}
                    textColor={theme.palette.grey[0] || '#fff'}
                    hoverBgColor={theme.palette.primary.dark}
                    hoverTextColor="#fff"
                    minWidth="276px"
                    startIcon={
                      sweeping ? (
                        <CircularProgress size={16} color="inherit" />
                      ) : undefined
                    }
                  >
                    {sweeping ? 'Sweeping...' : 'Run sweep now'}
                  </AccountActionButton>
                  <AccountActionButton
                    variant="outlined"
                    onClick={loadRetention}
                    disabled={retentionLoading || sweeping}
                    minWidth="156px"
                  >
                    Refresh
                  </AccountActionButton>
                </Box>
              </>
            ) : (
              <SettingDescription>Status unavailable</SettingDescription>
            )}
          </Section>
        </SettingsCard>
      </SettingsBlock>

      <SettingsBlock>
        <SettingsCard elevation={0}>
          <Section>
            <SectionLabel>Quote Lifecycle</SectionLabel>
            <SettingDescription sx={{ mb: 1 }}>
              Quote bookings are soft-deleted after their validity window
              expires, then permanently removed once they have sat in the
              recycle bin long enough.
            </SettingDescription>

            {quoteCleanupLoading && !quoteCleanup ? (
              <Box sx={{ py: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : quoteCleanup ? (
              <>
                <RetentionGrid>
                  <RetentionStat>
                    <StatLabel>Validity window</StatLabel>
                    <StatValue>{quoteCleanup.retentionDays} days</StatValue>
                  </RetentionStat>
                  <RetentionStat>
                    <StatLabel>Hard-delete after</StatLabel>
                    <StatValue>
                      {quoteCleanup.hardDeleteRetentionDays} days
                    </StatValue>
                  </RetentionStat>
                  <RetentionStat>
                    <StatLabel>Sweep every</StatLabel>
                    <StatValue>
                      {quoteCleanup.sweepIntervalHours} hours
                    </StatValue>
                  </RetentionStat>
                  <RetentionStat>
                    <StatLabel>Last soft-delete sweep</StatLabel>
                    <StatValue>
                      {formatDateTime(quoteCleanup.lastSweepAt)}
                    </StatValue>
                  </RetentionStat>
                  <RetentionStat>
                    <StatLabel>Next sweep</StatLabel>
                    <StatValue>
                      {formatDateTime(quoteCleanup.nextSweepAt)}
                    </StatValue>
                  </RetentionStat>
                  <RetentionStat>
                    <StatLabel>Last soft-deleted</StatLabel>
                    <StatValue>
                      {quoteCleanup.lastSweepDeletedCount ?? 0}
                    </StatValue>
                  </RetentionStat>
                  <RetentionStat>
                    <StatLabel>Awaiting soft-delete</StatLabel>
                    <StatValue>{quoteCleanup.expiredCount}</StatValue>
                  </RetentionStat>
                  <RetentionStat>
                    <StatLabel>Last hard-delete sweep</StatLabel>
                    <StatValue>
                      {formatDateTime(quoteCleanup.lastHardDeleteAt)}
                    </StatValue>
                  </RetentionStat>
                  <RetentionStat>
                    <StatLabel>Last hard-deleted</StatLabel>
                    <StatValue>
                      {quoteCleanup.lastHardDeletedCount ?? 0}
                    </StatValue>
                  </RetentionStat>
                  <RetentionStat>
                    <StatLabel>In recycle bin</StatLabel>
                    <StatValue>{quoteCleanup.softDeletedCount}</StatValue>
                  </RetentionStat>
                </RetentionGrid>

                <Box sx={{ mt: 1.5 }}>
                  <AccountActionButton
                    variant="outlined"
                    onClick={loadQuoteCleanup}
                    disabled={quoteCleanupLoading}
                    minWidth="156px"
                  >
                    Refresh
                  </AccountActionButton>
                </Box>
              </>
            ) : (
              <SettingDescription>Status unavailable</SettingDescription>
            )}
          </Section>
        </SettingsCard>
      </SettingsBlock>
    </Box>
  );
};

export default SystemSettings;
